<template>
  <span id="add-minimum">
    <el-button
      v-if="showButtonNew()"
      id="add-minimum-button"
      class="button-action-default"
      size="mini"
      type="success"
      plain
      @click="toggleVisible"
    >{{ $t('daysOfService.newDayOfServiceButton') }}</el-button>
    <i v-if="isEditMode" class="el-icon-edit header-info-dialog-value icon-action" id="btn-unit-detail" @click="toggleVisible" size="mini" icon="el-icon-plus"></i>

    <el-dialog
      id="add-master-rate-dialog"
      :title="isEditMode ? 'Edit Master Rate' : 'Add Master Rates'"
      :visible.sync="dialogVisible"
      width="60%"
      class="dialog-with-cols"
      :close-on-click-modal="false"
      append-to-body
      :before-close="closeDialog"
    >
        <el-form :model="form" :rules="formRules" ref="form" label-position="top" size="mini">
          <el-row>
            <el-col :span="8" class="form-vertical-item">
              <el-form-item :label="$t('masterRates.chargeCodes.name')" prop="chargeCode" ref="chargeCode">
                <template v-if="isEditMode">
                  <el-input
                    :value="form.chargeCodeName"
                    disabled
                    size="mini"
                  />
                </template>
                <template v-else>
                  <autocomplete
                    :model.sync="form.chargeCode"
                    :url="urlChargeCode"
                    :placeholder="$t('masterRates.chargeCodes.searchChargeCodeOrName')"
                    labelField="name"
                    popperAppendToBody
                    labelFieldLastWithDash="code"
                    valueField="id"
                    ref="autocomplete-input"
                    @select="onChargeCodeSelect"
                  />
                </template>
              </el-form-item>
            </el-col>

            <el-col :span="8" class="form-vertical-item">
              <el-form-item :label="$t('masterRates.warehouses')" prop="warehouse" ref="warehouse">
                <select-list-billing
                    :value.sync="form.warehouse"
                    :url="urlWarehouses"
                    :default-all="true"
                    :multiple="true"
                    :select-all-as-default="selectAllWarehouses"
                    label-field="code"
                    value-field="id"
                    ref="selectWarehouse"
                    @all-selected="(val) => selectAllWarehouses = val"
                />
                <el-checkbox
                    v-model="selectAllWarehouses"
                    @change="markAllWarehousesAsSelectedChecked"
                >{{ $t('masterRates.selectAllWarehouses') }}</el-checkbox>
              </el-form-item>
            </el-col>

            <el-col :span="8" class="form-vertical-item">
              <el-form-item :label="$t('masterRates.rate')" prop="minimumRate">
                <el-input
                    v-model="minimumRate"
                    clearable
                    size="mini"
                    class="input-search"
                    type="text"
                    @input="formatCurrency"
                    @blur="formatCurrencyeBlur"
                    autocomplete="off"
                  >
                  </el-input>
              </el-form-item>
            </el-col>
          </el-row>

          <el-row>
            <el-col :span="8" class="form-vertical-item">
              <el-form-item :label="$t('masterRates.management.effectiveDate')" prop="effectiveDate" ref="effectiveDate">
                <el-date-picker
                  v-model="form.effectiveDate"
                  class="el-dropdown-new drop-sp date-dob"
                  :placeholder="$t('masterRates.placeholder.effectiveDate')"
                  size="mini"
                  style="width: 100%"
                  format="MM/dd/yyyy"
                  :clearable="false"
                  type="date"
                  :disabled="isSelectedDateSent"
                />
              </el-form-item>
            </el-col>

            <el-col :span="8" class="form-vertical-item">
              <el-form-item :label="$t('masterRates.management.expirationDate')" prop="expirationDate" ref="expirationDate">
                <el-date-picker
                  v-model="form.expirationDate"
                  class="el-dropdown-new drop-sp date-dob"
                  :placeholder="$t('masterRates.placeholder.expirationDate')"
                  size="mini"
                  style="width: 100%"
                  format="MM/dd/yyyy"
                  :clearable="false"
                  type="date"
                />
              </el-form-item>
            </el-col>
          </el-row>
        </el-form>
        <div
          id="add-master-rate-dialog-footer"
          slot="footer"
          class="dialog-footer form-vertical-item flex-reverse-direction"
        >
          <el-button
            type="primary"
            id="save-storage-location-button"
            @click="submitForm()"
            size="mini"
          >{{ $t('common.save') }}</el-button>
          <el-button
            id="refresh-storage-location-button"
            @click="resetForm()"
            size="mini"
          >{{ $t('common.clear') }}</el-button>
        </div>
    </el-dialog>
  </span>
</template>

<script>

import { Message } from 'element-ui';
import { mapGetters } from 'vuex';
import constants from '@/utils/constants';
import { MASTER_RATES_MANAGEMENT_SEARCH } from '@/store/actions';
import apiConstants from '@/utils/apiConstants';
import Autocomplete from '@/components/Autocomplete/index';
import SelectListBilling from '@/components/billing/SelectListBilling/SelectListBilling';

export default {
  name: 'AddMasterRate',
  components: {
    SelectListBilling,
    Autocomplete
  },
  props: {
    masterRate: {
      type: Object,
      required: false,
      default: () => ({})
    },
    selectedDate: {
      type: String,
      required: false,
      default: null
    }
  },
  computed: {
    ...mapGetters([
      'orderEntry',
      'masterRatesManagement'
    ]),
    isEditMode() {
      return this.masterRate && Object.keys(this.masterRate).length > 0;
    },
    selectedConsignee() {
      return this.masterRate.actualFilters.consigneeAccountId;
    },
  },
  data() {
    return {
      dialogVisible: false,
      loading: false,
      isSelectedDateSent: false,
      minimumRate: null,
      selectAllWarehouses: true,
      baseAdjustmentUnitType: null,
      form: {
        chargeCode: null,
        warehouse: [],
        effectiveDate: null,
        expirationDate: null,
        minimumRate: null
      },
      formRules: {
        chargeCode: [
          {
            required: true,
            message: 'Charge code is required',
          }
        ],
        minimumRate: [
          {
            required: true,
            message: 'Field is required',
            trigger: 'blur'
          },
          {
            validator: (rule, value, callback) => {
              if (value === null || value === '' || value === undefined) {
                callback(new Error('Field is required'));
              } else {
                callback();
              }
            },
            trigger: ['blur', 'change']
          }
        ],
        effectiveDate: [
          {
            required: true,
            message: this.$t('minimum.error.effectiveDateRequired'),
            trigger: 'blur'
          }
        ],
      },
      urlWarehouses: `${apiConstants.END_POINTS.ACCOUNTS.DAYS_OF_SERVICE.MANAGEMENT.WAREHOUSE}`,
      urlChargeCode: apiConstants.END_POINTS.BILLING.CHARGE_CODE_LIST_SEARCH,
    };
  },
  methods: {
    showButtonNew() {
      return this.$can(this.permissions.DAY_OF_SERVICE.EDIT_DAY_OF_SERVICE) && !this.isEditMode;
    },
    onAmountToInput(val) {
      this.form.amountTo = val === '' ? null : val;
    },
    toggleVisible() {
      this.dialogVisible = !this.dialogVisible;
      if (this.dialogVisible) {
        this.fillForm();
      }
    },
    resetForm() {
      if (this.isEditMode) {
        /*
        this.form.minimumRate = null;
        this.form.warehouse = [];
        this.form.effectiveDate = this.selectedDate ? new Date(`${this.selectedDate}T00:00:00`) : null;
        this.form.expirationDate = null;
*/

        this.fillForm();
      } else {
        this.selectAllWarehouses = true;
        this.markAllWarehousesAsSelectedChecked();
        this.minimumRate = null;
        this.form.chargeCode = null;
        this.form = {
          chargeCodeName: null,
          chargeCode: null,
          warehouse: [],
          effectiveDate: this.selectedDate ? new Date(`${this.selectedDate}T00:00:00`) : null,
          expirationDate: null,
          minimumRate: null
        };
        if (this.$refs['autocomplete-input']) {
          this.$refs['autocomplete-input'].clearLocalModel();
        }
      }
      this.$refs.form.resetFields();
      if (this.$refs['filter-region-select']) {
        this.$refs['filter-region-select'].setLocalModel(null);
      }
      // Limpia el select-list de Warehouses (region)
      /* if (this.$refs['filter-region-select-warehouse']) {
        this.$refs['filter-region-select-warehouse'].setLocalModel(null);
      } */
    },
    closeDialog() {
      this.dialogVisible = false;
      this.resetForm();
    },
    getDateFromTime(time) {
      const hour = time.split(':')[0];
      const minute = time.split(':')[1];
      const date = new Date();

      date.setHours(hour);
      date.setMinutes(minute);
      date.setSeconds(0);

      return date;
    },
    getWarehouseIds(ids) {
      if (!ids) {
        return [];
      }
      if (ids instanceof Array) {
        return ids;
      }

      if (ids.includes(',')) {
        return ids.split(',').map(id => parseInt(id.trim(), 10));
      }
      return [parseInt(ids.trim(), 10)];
    },
    async submitForm() {
      if (this.form.warehouse === null || this.form.warehouse.length === 0) {
        this.markAllWarehousesAsSelected();
      }
      this.form.minimumRate = this.validateRateOnlyNumbers(this.form.minimumRate); // ? parseFloat(this.form.rate) : 0;

      return new Promise((resolve) => {
        this.$refs.form.validate((valid) => {
          if (valid) {
            Message.closeAll();
            const loading = this.$loading(constants.LOADING.DEFAULT_CONFIG);
            this.$store
              .dispatch('masterRatesManagement/save', { ...this.form })
              .then((response) => {
                loading.close();
                this.$message.success(response.message);
                this.closeDialog();
                this.$emit('refresh-list');
                this.$store.dispatch(MASTER_RATES_MANAGEMENT_SEARCH, {});
                resolve(response);
              })
              .catch((error) => {
                loading.close();
                resolve(error);
              });
          } else {
            resolve(new Error(this.$t('common.invalid')));
          }
        });
      });
    },
    getTodayDate() {
      const today = new Date();
      const year = today.getFullYear();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    fillForm() {
      if (this.isEditMode) {
        this.form.id = this.masterRate.id;
        this.form.chargeCodeName = this.masterRate.chargeCodeName;
        this.form.chargeCode = this.masterRate.chargeCodeId;
        this.form.dayOfServiceType = this.masterRate.recordType;
        this.form.minimumRate = this.masterRate.rate;
        this.minimumRate = this.$options.filters.rateFilter(this.masterRate.rate, this.masterRate.unitsOfMeasure === '%');
        this.baseAdjustmentUnitType = this.masterRate.unitsOfMeasure;

        if (Array.isArray(this.masterRate.warehouseCode)) {
          this.form.warehouse = [...this.masterRate.warehouseCode];
        } else if (typeof this.masterRate.warehouseCode === 'string') {
          this.form.warehouse = this.masterRate.warehouseCode.split(',').map(code => code.trim());
        } else {
          this.form.warehouse = [];
        }
        this.selectAllWarehouses = this.masterRate.hasAllWarehouses;

        const [month, days, year] = this.masterRate.effectiveDate.split('/');
        this.form.effectiveDate = new Date(Number(year), Number(month) - 1, Number(days));

        if (this.masterRate.expirationDate !== null) {
          const [expirationMonth, expirationDay, expirationYear] = this.masterRate.expirationDate.split('/');
          this.form.expirationDate = new Date(Number(expirationYear), Number(expirationMonth) - 1, Number(expirationDay));
        } else {
          this.form.expirationDate = null;
        }

        this.$nextTick(() => {
          if (this.$refs.selectWarehouse) {
            this.$refs.selectWarehouse.setLocalModel(this.form.warehouse);
          }
        });
      }
    },
    formatCurrency(value) {
      const filtered = value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
      this.minimumRate = filtered;
    },
    formatCurrencyeBlur() {
      this.form.minimumRate = this.minimumRate;
      this.minimumRate = this.$options.filters.rateFilter(this.minimumRate, this.baseAdjustmentUnitType === '%');
    },
    onChargeCodeSelect(item) {
      this.chargeCodeCode = item.code;
      this.baseAdjustmentUnitType = item.unitsOfMeasure;
      this.formatCurrencyeBlur();
    },
    markAllWarehousesAsSelected() {
      if (this.$refs.selectWarehouse) {
        this.$refs.selectWarehouse.selectAllValues();
      }
    },
    markAllWarehousesAsSelectedChecked() {
      if (this.selectAllWarehouses) {
        this.$refs.selectWarehouse.selectAllValues();
      } else {
        this.$refs.selectWarehouse.clearLocalModel();
        this.form.warehouse = [];
      }
    },
    validateRateOnlyNumbers(value) {
      if (value === null || value === undefined) return null;
      if (typeof value === 'number') return value;
      return value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
    },
  },
  watch: {
    selectedDate: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          const [year, month, days] = newValue.split('-');
          this.form.effectiveDate = new Date(Number(year), Number(month) - 1, Number(days));

          const date = new Date(newValue);
          const dayOfWeek = date.getDay();
          const matchedDay = constants.DAY_OF_SERVICE.DAYS_OF_WEEK.find(
            (day) => day.numberValue === (dayOfWeek === 0 ? 1 : dayOfWeek + 1)
          );
          this.form.day = matchedDay ? matchedDay.numberValue : null;

          this.isSelectedDateSent = true;
        } else {
          this.form.effectiveDate = null;
          this.form.day = null;
          this.isSelectedDateSent = false;
        }
      },
    },
  },
};
</script>
